import React, { useState, useCallback, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';

import * as Styled from './styles';
import { CookieConsentProps, CookieConsentPreferences } from './types';
import { useCookieConsent } from './hooks/useCookieConsent';

import { IconCloseCircle } from '../../../assets/svg';
import RichText from '../../molecules/RichText';
import { LinkMode } from '../../atoms/Link/enums';

const CookieConsent: React.FC<CookieConsentProps> = ({ data, onShowSettingsReady }) => {
  const {
    title,
    description,
    acceptAllLabel,
    rejectAllLabel,
    customizeLabel,
    saveLabel,
    privacyPolicyLabel,
    privacyPolicyUrl,
    cookiePolicyLabel,
    cookiePolicyUrl,
    categories,
    consentDuration,
    isActive
  } = data;

  const [showSettings, setShowSettings] = useState(false);
  const [tempPreferences, setTempPreferences] = useState<CookieConsentPreferences>({});

  const {
    isVisible,
    preferences,
    saveConsent,
    acceptAll,
    rejectAll,
    hideConsentBanner
  } = useCookieConsent(
    categories.map(cat => ({ id: cat.id, isRequired: cat.isRequired })),
    consentDuration
  );

  const handleAcceptAll = useCallback(() => {
    acceptAll(categories.map(cat => cat.id));
  }, [acceptAll, categories]);

  const handleRejectAll = useCallback(() => {
    const requiredCategories = categories
      .filter(cat => cat.isRequired)
      .map(cat => cat.id);
    rejectAll(requiredCategories);
  }, [rejectAll, categories]);

  const handleCustomize = useCallback(() => {
    // Initialize temp preferences with current state or defaults
    const initialPrefs: CookieConsentPreferences = {};
    categories.forEach(category => {
      initialPrefs[category.id] = preferences[category.id] !== undefined 
        ? preferences[category.id] 
        : category.isEnabledByDefault || category.isRequired;
    });
    setTempPreferences(initialPrefs);
    setShowSettings(true);
  }, [categories, preferences]);

  const handleSavePreferences = useCallback(() => {
    saveConsent(tempPreferences);
    setShowSettings(false);
  }, [saveConsent, tempPreferences]);

  const handleToggleCategory = useCallback((categoryId: string, enabled: boolean) => {
    setTempPreferences(prev => ({
      ...prev,
      [categoryId]: enabled
    }));
  }, []);

  const handleCloseSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  // Expose the showSettings function to parent component
  useEffect(() => {
    if (onShowSettingsReady) {
      onShowSettingsReady(() => {
        // Initialize temp preferences when opening settings
        const initialPrefs: CookieConsentPreferences = {};
        categories.forEach(category => {
          initialPrefs[category.id] = preferences[category.id] !== undefined 
            ? preferences[category.id] 
            : category.isEnabledByDefault || category.isRequired;
        });
        setTempPreferences(initialPrefs);
        setShowSettings(true);
      });
    }
  }, [onShowSettingsReady, categories, preferences]);

  // Modal-specific handlers that update temp preferences and save
  const handleModalAcceptAll = useCallback(() => {
    const allAcceptedPrefs: CookieConsentPreferences = {};
    categories.forEach(category => {
      allAcceptedPrefs[category.id] = true;
    });
    setTempPreferences(allAcceptedPrefs);
    saveConsent(allAcceptedPrefs);
    setShowSettings(false);
  }, [categories, saveConsent]);

  const handleModalRejectAll = useCallback(() => {
    const requiredOnlyPrefs: CookieConsentPreferences = {};
    categories.forEach(category => {
      requiredOnlyPrefs[category.id] = category.isRequired;
    });
    setTempPreferences(requiredOnlyPrefs);
    saveConsent(requiredOnlyPrefs);
    setShowSettings(false);
  }, [categories, saveConsent]);

  // Listen for external cookie settings trigger
  useEffect(() => {
    const handleOpenSettings = () => {
      if (!isVisible) {
        handleCustomize();
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('openCookieSettings', handleOpenSettings);
      return () => window.removeEventListener('openCookieSettings', handleOpenSettings);
    }
  }, [isVisible, handleCustomize]);

  // Don't render if not active
  if (!isActive) return null;

  return (
    <>
      {/* Main Cookie Banner */}
      <AnimatePresence>
        {isVisible && !showSettings && (
          <Styled.CookieConsentBanner
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: '0' }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ duration: 0.3, type: 'tween' }}
          >
            <Styled.CookieConsentInner>
              <Styled.CookieConsentContent>
                <Styled.CookieConsentText>
                  <Styled.CookieConsentTitle variant="headingL">
                    {title}
                  </Styled.CookieConsentTitle>
                  <Styled.CookieConsentDescription>
                    <RichText data={description} />
                  </Styled.CookieConsentDescription>
                  <Styled.CookieConsentLinks>
                    <Styled.CookieConsentLink 
                      to={privacyPolicyUrl} 
                      mode={LinkMode.HasPlainLook}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {privacyPolicyLabel}
                    </Styled.CookieConsentLink>
                    <Styled.CookieConsentLink 
                      to={cookiePolicyUrl} 
                      mode={LinkMode.HasPlainLook}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {cookiePolicyLabel}
                    </Styled.CookieConsentLink>
                  </Styled.CookieConsentLinks>
                </Styled.CookieConsentText>

                <Styled.CookieConsentActions>
                  <Styled.CookieConsentPrimaryButton onClick={handleAcceptAll}>
                    {acceptAllLabel}
                  </Styled.CookieConsentPrimaryButton>
                  <Styled.CookieConsentSecondaryButton onClick={handleRejectAll}>
                    {rejectAllLabel}
                  </Styled.CookieConsentSecondaryButton>
                  <Styled.CookieConsentSecondaryButton onClick={handleCustomize}>
                    {customizeLabel}
                  </Styled.CookieConsentSecondaryButton>
                </Styled.CookieConsentActions>
              </Styled.CookieConsentContent>
            </Styled.CookieConsentInner>
          </Styled.CookieConsentBanner>
        )}
      </AnimatePresence>

      {/* Cookie Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <Styled.CookieSettingsModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleCloseSettings}
          >
            <Styled.CookieSettingsContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Styled.CookieSettingsHeader>
                <Styled.CookieSettingsTitle variant="headingL">
                  Cookie Settings
                </Styled.CookieSettingsTitle>
                <Styled.CookieSettingsClose onClick={handleCloseSettings}>
                  <IconCloseCircle />
                </Styled.CookieSettingsClose>
              </Styled.CookieSettingsHeader>

              <div>
                <Styled.CookieConsentDescription>
                  <RichText data={description} />
                </Styled.CookieConsentDescription>

                {categories.map((category) => (
                  <Styled.CookieCategory key={category.id}>
                    <Styled.CookieCategoryHeader>
                      <Styled.CookieCategoryInfo>
                        <Styled.CookieCategoryName variant="headingS">
                          {category.name}
                          {category.isRequired && ' (Required)'}
                        </Styled.CookieCategoryName>
                        <Styled.CookieCategoryDescription>
                          {category.description}
                        </Styled.CookieCategoryDescription>
                      </Styled.CookieCategoryInfo>

                      <Styled.CookieCategoryToggle>
                        <Styled.CookieToggleSwitch disabled={category.isRequired}>
                          <Styled.CookieToggleInput
                            type="checkbox"
                            checked={tempPreferences[category.id] || false}
                            disabled={category.isRequired}
                            onChange={(e) => handleToggleCategory(category.id, e.target.checked)}
                          />
                          <Styled.CookieToggleSlider />
                        </Styled.CookieToggleSwitch>
                      </Styled.CookieCategoryToggle>
                    </Styled.CookieCategoryHeader>

                    {category.cookies && category.cookies.length > 0 && (
                      <Styled.CookieDetailsTable>
                        <thead>
                          <tr>
                            <th>Cookie Name</th>
                            <th>Purpose</th>
                            <th>Duration</th>
                            <th>Provider</th>
                          </tr>
                        </thead>
                        <tbody>
                          {category.cookies.map((cookie, index) => (
                            <tr key={index}>
                              <td>{cookie.name}</td>
                              <td>{cookie.purpose}</td>
                              <td>{cookie.duration}</td>
                              <td>{cookie.provider}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Styled.CookieDetailsTable>
                    )}
                  </Styled.CookieCategory>
                ))}

                <Styled.CookieSettingsDisclaimer>
                  <strong>Important:</strong> Disabling certain cookies may impact your experience on our website. 
                  Required cookies cannot be disabled as they are essential for the website to function properly. 
                  You can change your preferences at any time by clicking the cookie settings link in our footer.
                </Styled.CookieSettingsDisclaimer>

                <Styled.CookieSettingsActions>
                  <Styled.CookieConsentPrimaryButton onClick={handleSavePreferences}>
                    {saveLabel}
                  </Styled.CookieConsentPrimaryButton>
                  <Styled.CookieConsentSecondaryButton onClick={handleModalAcceptAll}>
                    {acceptAllLabel}
                  </Styled.CookieConsentSecondaryButton>
                  <Styled.CookieConsentSecondaryButton onClick={handleModalRejectAll}>
                    {rejectAllLabel}
                  </Styled.CookieConsentSecondaryButton>
                </Styled.CookieSettingsActions>
              </div>
            </Styled.CookieSettingsContent>
          </Styled.CookieSettingsModal>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsent;
